import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Clock, 
  Shield, 
  Eye,
  Heart,
  Filter,
  Search
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface MarketplaceListing {
  id: string;
  drop: {
    id: string;
    name: string;
    rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
    media_url: string;
    serial_number: number;
    total_count: number;
  };
  seller: {
    id: string;
    username: string;
    display_name: string;
  };
  price_cents: number;
  floor_price_cents?: number;
  trending_direction?: 'up' | 'down' | 'stable';
  price_change_percent?: number;
  listed_at: string;
  views_count: number;
  in_escrow: boolean;
  escrow_expires_at?: string;
}

const DropsMarketplace: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [filterRarity, setFilterRarity] = useState('all');

  // Placeholder data - this will come from Supabase
  const placeholderListings: MarketplaceListing[] = [
    {
      id: '1',
      drop: {
        id: 'd1',
        name: 'Neon Collectible',
        rarity: 'legendary',
        media_url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400',
        serial_number: 3,
        total_count: 10,
      },
      seller: {
        id: 's1',
        username: 'cryptowhale',
        display_name: 'Crypto Whale',
      },
      price_cents: 15000, // $150
      floor_price_cents: 12000,
      trending_direction: 'up',
      price_change_percent: 25,
      listed_at: new Date(Date.now() - 3600000).toISOString(),
      views_count: 234,
      in_escrow: false,
    },
    {
      id: '2',
      drop: {
        id: 'd2',
        name: 'Cosmic Drop',
        rarity: 'rare',
        media_url: 'https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400',
        serial_number: 15,
        total_count: 70,
      },
      seller: {
        id: 's2',
        username: 'collector99',
        display_name: 'Collector 99',
      },
      price_cents: 7500, // $75
      floor_price_cents: 6500,
      trending_direction: 'up',
      price_change_percent: 15,
      listed_at: new Date(Date.now() - 7200000).toISOString(),
      views_count: 156,
      in_escrow: true,
      escrow_expires_at: new Date(Date.now() + 3600000).toISOString(),
    },
    {
      id: '3',
      drop: {
        id: 'd3',
        name: 'Digital Artifact',
        rarity: 'uncommon',
        media_url: 'https://images.unsplash.com/photo-1618556450994-a6a128ef0d9d?w=400',
        serial_number: 42,
        total_count: 400,
      },
      seller: {
        id: 's3',
        username: 'artlover',
        display_name: 'Art Lover',
      },
      price_cents: 3000, // $30
      floor_price_cents: 2800,
      trending_direction: 'stable',
      listed_at: new Date(Date.now() - 86400000).toISOString(),
      views_count: 89,
      in_escrow: false,
    },
  ];

  const rarityColors = {
    common: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-300', dot: 'bg-emerald-500' },
    uncommon: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-300', dot: 'bg-blue-500' },
    rare: { bg: 'bg-purple-500/20', border: 'border-purple-500/40', text: 'text-purple-300', dot: 'bg-purple-500' },
    legendary: { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-300', dot: 'bg-orange-500' },
  };

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const formatTimeAgo = (dateString: string) => {
    const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Listings</p>
                <p className="text-2xl font-bold">127</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">24h Volume</p>
                <p className="text-2xl font-bold">$12,450</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
            </div>
            <p className="text-xs text-green-400 mt-1">+18.2% from yesterday</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Sale Price</p>
                <p className="text-2xl font-bold">$87.50</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Last 7 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Escrow</p>
                <p className="text-2xl font-bold">23</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-orange-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Pending transfer</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, collection, or seller..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={filterRarity} onValueChange={setFilterRarity}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by rarity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rarities</SelectItem>
                <SelectItem value="common">Common</SelectItem>
                <SelectItem value="uncommon">Uncommon</SelectItem>
                <SelectItem value="rare">Rare</SelectItem>
                <SelectItem value="legendary">Legendary</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Recently Listed</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="trending">Most Popular</SelectItem>
                <SelectItem value="ending">Ending Soon</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="icon">
              <Filter className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Listings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {placeholderListings.map((listing) => {
          const color = rarityColors[listing.drop.rarity];
          const isPriceAboveFloor = listing.floor_price_cents && listing.price_cents > listing.floor_price_cents;

          return (
            <Card 
              key={listing.id} 
              className="group overflow-hidden hover:shadow-2xl transition-all cursor-pointer border-2 hover:border-primary/50 relative"
            >
              {/* Escrow Badge */}
              {listing.in_escrow && (
                <div className="absolute top-3 right-3 z-10">
                  <Badge className="bg-orange-500/90 text-white border-0 flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    In Escrow
                  </Badge>
                </div>
              )}

              {/* Drop Image */}
              <div className="relative h-64 overflow-hidden bg-gradient-to-br from-purple-500/10 to-pink-500/10">
                <img
                  src={listing.drop.media_url}
                  alt={listing.drop.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />

                {/* Rarity Badge */}
                <div className="absolute bottom-3 left-3">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${color.border} ${color.bg} backdrop-blur-sm`}>
                    <div className={`w-2 h-2 rounded-full ${color.dot}`} />
                    <span className={`text-xs font-semibold ${color.text} capitalize`}>
                      {listing.drop.rarity}
                    </span>
                  </div>
                </div>

                {/* Serial Number */}
                <div className="absolute bottom-3 right-3">
                  <div className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/10">
                    <span className="text-xs font-mono text-white">
                      #{listing.drop.serial_number}/{listing.drop.total_count}
                    </span>
                  </div>
                </div>
              </div>

              <CardContent className="p-5 space-y-4">
                {/* Drop Name */}
                <div>
                  <h3 className="font-bold text-lg mb-1">{listing.drop.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    by @{listing.seller.username}
                  </p>
                </div>

                {/* Price Section */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">List Price</span>
                    {listing.trending_direction && (
                      <div className="flex items-center gap-1">
                        {listing.trending_direction === 'up' ? (
                          <>
                            <TrendingUp className="w-3 h-3 text-green-400" />
                            <span className="text-xs text-green-400">
                              +{listing.price_change_percent}%
                            </span>
                          </>
                        ) : listing.trending_direction === 'down' ? (
                          <>
                            <TrendingDown className="w-3 h-3 text-red-400" />
                            <span className="text-xs text-red-400">
                              -{listing.price_change_percent}%
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">
                      {formatPrice(listing.price_cents)}
                    </span>
                    {listing.floor_price_cents && (
                      <span className="text-sm text-muted-foreground">
                        Floor: {formatPrice(listing.floor_price_cents)}
                      </span>
                    )}
                  </div>

                  {isPriceAboveFloor && (
                    <Badge variant="outline" className="text-xs">
                      {Math.round(((listing.price_cents - listing.floor_price_cents!) / listing.floor_price_cents!) * 100)}% above floor
                    </Badge>
                  )}
                </div>

                {/* Stats Row */}
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      <span>{listing.views_count}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{formatTimeAgo(listing.listed_at)}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Heart className="w-4 h-4" />
                  </Button>
                </div>

                {/* Action Button */}
                <Button 
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  disabled={listing.in_escrow}
                >
                  {listing.in_escrow ? 'In Escrow' : 'Buy Now'}
                </Button>

                {listing.in_escrow && listing.escrow_expires_at && (
                  <p className="text-xs text-center text-muted-foreground">
                    Escrow expires {formatTimeAgo(listing.escrow_expires_at)}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State (when no listings) */}
      {placeholderListings.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <DollarSign className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No listings yet</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Be the first to list your drop on the marketplace! 
              Set your price and start earning.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DropsMarketplace;
